import type { Dispatch } from '@reduxjs/toolkit'

import type { CompletedSaleTransaction } from '~/api/graphql/generated/types'
import type { PaymentData } from '~/constants/addPaymentDialogV2Constants'
import {
  fetchBatchInvoice,
  refreshBalanceHistory,
} from '~/store/actions/finance'
import { fetchInvoiceV3 } from '~/store/reducers/invoiceV3'
import type { InvoiceOrEstimate } from '~/types'

import { addDecimals, subtractDecimals } from './clientAccountCreditUtils'

/**
 * Creates updated payment data with transaction details from a completed sale.
 * Maps transaction fields to payment data fields for storing transaction info.
 */
export const createUpdatedPaymentData = (
  currentPaymentData: PaymentData,
  transaction: CompletedSaleTransaction,
): PaymentData => ({
  ...currentPaymentData,
  creditCardLastFour: transaction.cardDetails?.lastFour,
  creditCardType: transaction.cardDetails?.type?.name,
  paidBy: transaction.cardDetails?.paidBy || '',
  rhapsodyTransactionId: transaction.id || '',
  braintreeTransactionId: transaction.externalProcessorId || '',
  originalInvoiceDue: currentPaymentData.originalInvoiceDue,
})

// Helper function to determine if a payment is partial or full
export interface PaymentCheckResult {
  isPartialPayment: boolean
  remainingBalance: number
  totalPaymentAmount: number
}

export const checkPaymentType = (
  paymentData: PaymentData,
  invoiceDue: number,
): PaymentCheckResult => {
  const paymentAmount = parseFloat(paymentData.amountToCharge || '0')
  const accountCreditAmount = paymentData.accountCreditTotal || 0
  const totalPaymentAmount = addDecimals(paymentAmount, accountCreditAmount)
  const remainingBalance = subtractDecimals(invoiceDue, totalPaymentAmount)

  return {
    isPartialPayment: remainingBalance > 0,
    remainingBalance,
    totalPaymentAmount,
  }
}

export interface RefreshInvoiceOptions {
  isEstimate?: boolean
  refetchInvoice?: () => void
}

/**
 * Refreshes the invoice and balance history after payment operations.
 * Handles both single and multiple invoice scenarios.
 * For estimates, uses the provided refetchInvoice callback to update the finance store.
 * For invoices, uses fetchInvoiceV3 to update the invoiceV3 store.
 */
export const refreshInvoiceAndBalanceHistory = (
  invoice: InvoiceOrEstimate,
  dispatch: Dispatch<any>,
  options?: RefreshInvoiceOptions,
): void => {
  if (options?.isEstimate && options?.refetchInvoice) {
    options.refetchInvoice()
  } else if (!invoice.id.includes(',')) {
    dispatch(fetchInvoiceV3({ id: invoice.id }))
  } else {
    const invoiceIds = invoice.id.split(',')
    dispatch(fetchBatchInvoice(invoiceIds))
  }

  if (invoice.clientId) {
    dispatch(refreshBalanceHistory(invoice.clientId))
  }
}

export const resetCancelState = (
  paymentDialogDispatch: React.Dispatch<any>,
) => {
  paymentDialogDispatch({
    type: 'SET_IS_CANCELLING',
    payload: false,
  })
  paymentDialogDispatch({
    type: 'CANCEL_TRANSACTION',
  })
}

export const handleCancelErrorForPartialPayment = (
  paymentDialogDispatch: React.Dispatch<any>,
  message: string,
) => {
  paymentDialogDispatch({
    type: 'SET_CANCEL_ERROR',
    payload: {
      message,
    },
  })
  paymentDialogDispatch({
    type: 'SET_IS_CANCELLING',
    payload: false,
  })
}
export const extractBackendErrorMessage = (
  error: any,
  defaultMessage: string,
): string => (
    error?.message ||
    error?.graphQLErrors?.[0]?.message ||
    error?.networkError?.message ||
    defaultMessage
  )
